/**
 * A route-test container judges only its own drops, and its first drop has
 * to land before the country window when the master strategy is Route tests.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenGatewayRanking } from "../gateway-ranking/gateway-ranking.types";

export const ROUTE_TEST_KIND = "ROUTE_TEST";
export const ROUTE_TEST_STRATEGY = "ROUTE_TEST";
export const USE_REST_OF_DAY = "use_rest_of_day";

export type RouteTestSource = "drops" | "gateway";

/** Whose numbers a route test reads. Anything unsaid is this container's own drops. */
export function routeTestSource(ranking: { source?: string | null }): RouteTestSource {
  return ranking.source === "gateway" ? "gateway" : "drops";
}

export interface RouteTestSlot {
  enabled: boolean;
  orderIndex: number;
  pinnedAtMin?: number | null;
}

export interface RouteTestClock {
  enabled: boolean;
  routeTest: boolean;
  strategy: string | null;
  startMode: string;
  startAtMin: number | null;
  slots: RouteTestSlot[];
}

/** The first enabled drop's minute, or null when it would start at the window. */
export function earliestDropMinute(clock: Omit<RouteTestClock, "enabled" | "routeTest" | "strategy">): number | null {
  const first = clock.slots.filter((slot) => slot.enabled).sort((a, b) => a.orderIndex - b.orderIndex)[0];
  if (!first) return null;
  if (first.pinnedAtMin != null) return first.pinnedAtMin;
  if (clock.startMode === "FIXED_TIME" && clock.startAtMin != null) return clock.startAtMin;
  return null;
}

/** Why a Route tests day cannot be generated, or null when it can. */
export function routeTestDayProblem(containers: RouteTestClock[], windowStartMin: number): string | null {
  if (!containers.some((c) => c.strategy === ROUTE_TEST_STRATEGY)) return null;
  const test = containers.find((c) => c.routeTest && c.enabled);
  if (!test) return "Route tests need one enabled route test container";
  const minute = earliestDropMinute(test);
  if (minute === null || minute >= windowStartMin) return "The route test's first drop must be before the country window";
  return null;
}

const TODAY = {
  dateRange: "today",
  dateRangeCustomFrom: undefined,
  dateRangeCustomTo: undefined,
};

/** Route-test formula groups and rules read today. A rule on the formula's first range stays with it. */
export function lockRouteTestRanking(ranking: DropGenGatewayRanking): DropGenGatewayRanking {
  return {
    formula: {
      ...ranking.formula,
      groups: ranking.formula.groups.map((group) => ({ ...group, ...TODAY })),
    },
    rules: (ranking.rules ?? []).map((rule) => (rule.dateRange === "first-range" ? rule : { ...rule, ...TODAY })),
    source: routeTestSource(ranking),
    routeTestSettleMin: ranking.routeTestSettleMin ?? 30,
    // 19/10/2026 — the texts of the test drops ride along (absent = the default).
    ...(ranking.routeTestTexts ? { routeTestTexts: ranking.routeTestTexts } : {}),
  };
}

export function routeTestRankingProblem(ranking: DropGenGatewayRanking): string | null {
  const group = ranking.formula.groups.some((g) => g.dateRange !== "today");
  const rule = (ranking.rules ?? []).some((r) => r.dateRange !== "today" && r.dateRange !== "first-range");
  return group || rule ? "A route test formula and its rules can only use today" : null;
}
